import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { propertyAPI, dashboardAPI } from '@/services/api';
import { Hotel, Calendar, DollarSign, TrendingUp, Users, ArrowUpRight, ArrowDownRight, Copy, Shield, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

import { useGlobalProperty } from '@/hooks/useGlobalProperty';

export default function Dashboard() {
  const { user } = useAuth();
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useGlobalProperty(properties);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [supportRequests, setSupportRequests] = useState([]);
  const [respondingTo, setRespondingTo] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Admin should go to admin dashboard
    if (user?.role === 'admin') {
      navigate('/admin');
      return;
    }
    fetchProperties();
  }, [user]);

  useEffect(() => {
    if (selectedProperty) {
      fetchStats();
    }
  }, [selectedProperty]);

  const fetchProperties = async () => {
    try {
      const response = await propertyAPI.getAll();
      setProperties(response.data);
      
      if (response.data.length === 0) {
        navigate('/onboarding');
      } else {
        // Filter to only show approved properties
        const approvedProperties = response.data.filter(p => p.estado_aprobacion === 'aprobada');
        if (approvedProperties.length > 0) {
          setSelectedProperty(approvedProperties[0].id);
        } else if (response.data.length > 0) {
          // If no approved, show first one but with pending notice
          
        }
      }
    } catch (error) {
      console.error('Error fetching properties:', error);
      toast.error('Error al cargar propiedades');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await dashboardAPI.getStats(selectedProperty);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Error al cargar estadísticas');
    }
  };

  // Fetch pending support access requests for this owner
  const fetchSupportRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${BACKEND_URL}/api/support-requests/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSupportRequests(res.data);
    } catch (error) {
      console.error('Error fetching support requests:', error);
    }
  };

  useEffect(() => {
    if (user?.role !== 'admin') {
      fetchSupportRequests();
    }
  }, [user]);

  const handleRespondAccess = async (requestId, autorizado) => {
    setRespondingTo(requestId);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/support-requests/${requestId}/respond`,
        { autorizado, motivo_rechazo: autorizado ? '' : 'Denegado por propietario' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(autorizado ? 'Acceso autorizado al administrador' : 'Solicitud de acceso denegada');
      fetchSupportRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al responder solicitud');
    } finally {
      setRespondingTo(null);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Cargando...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const selectedPropertyData = properties.find(p => p.id === selectedProperty);

  const copyCode = () => {
    if (selectedPropertyData?.codigo_unico) {
      navigator.clipboard.writeText(selectedPropertyData.codigo_unico);
      toast.success('Código copiado');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="dashboard-container">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold" style={{fontFamily: 'Manrope'}} data-testid="dashboard-title">
              Dashboard
            </h1>
            <p className="text-muted-foreground mt-1">
              Visión general de tu propiedad
            </p>
          </div>

          <div className="flex flex-col items-end gap-2">
            {properties.length > 0 && (
              <Select value={selectedProperty} onValueChange={setSelectedProperty}>
                <SelectTrigger className="w-full sm:w-[280px]" data-testid="property-selector">
                  <SelectValue placeholder="Selecciona una propiedad" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map((prop) => (
                    <SelectItem key={prop.id} value={prop.id}>
                      {prop.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {selectedPropertyData?.codigo_unico && (
              <button 
                onClick={copyCode}
                className="flex items-center gap-2 bg-primary/10 hover:bg-primary/20 text-primary rounded-full px-3 py-1 transition-colors cursor-pointer"
                data-testid="property-code-badge"
                title="Clic para copiar código"
              >
                <span className="text-xs font-bold tracking-wider">{selectedPropertyData.codigo_unico}</span>
                <Copy className="h-3 w-3" />
              </button>
            )}
          </div>
        </div>

        {/* Support Access Request Notifications */}
        {supportRequests.length > 0 && (
          <div className="space-y-3" data-testid="support-requests-banner">
            {supportRequests.map((req) => (
              <Card key={req.id} className="border-l-4 border-l-amber-500 bg-amber-50/80 shadow-md">
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Shield className="h-5 w-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-amber-900">Solicitud de Acceso de Soporte</p>
                        <p className="text-sm text-amber-800">
                          <strong>{req.admin_nombre}</strong> solicita acceso a <strong>{req.property_nombre}</strong> ({req.property_codigo})
                        </p>
                        <p className="text-sm text-amber-700 mt-1">
                          Motivo: {req.motivo} &middot; Duración: {req.duracion_horas}h
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-400 text-red-600 hover:bg-red-50"
                        onClick={() => handleRespondAccess(req.id, false)}
                        disabled={respondingTo === req.id}
                        data-testid={`deny-access-${req.id}`}
                      >
                        <XCircle className="h-4 w-4 mr-1" />
                        Denegar
                      </Button>
                      <Button
                        size="sm"
                        className="bg-slate-800 hover:bg-slate-700 text-white"
                        onClick={() => handleRespondAccess(req.id, true)}
                        disabled={respondingTo === req.id}
                        data-testid={`accept-access-${req.id}`}
                      >
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Autorizar
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Stats Grid */}
        {stats && (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            <Card className="stat-card border-none bg-gradient-to-br from-slate-700 to-slate-800 text-white" data-testid="stat-card-ocupacion">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-white/90">
                  Tasa de Ocupación
                </CardTitle>
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-white" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold" style={{fontFamily: 'Manrope'}}>
                  {stats.tasa_ocupacion}%
                </div>
                <p className="text-sm text-white/80 mt-2">
                  {stats.reservas_activas} reservas activas
                </p>
              </CardContent>
            </Card>

            <Card className="stat-card border-none bg-gradient-to-br from-blue-500 to-blue-600 text-white" data-testid="stat-card-ingresos-mes">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-white/90">
                  Ingresos del Mes
                </CardTitle>
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <DollarSign className="h-5 w-5 text-white" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold" style={{fontFamily: 'Manrope'}}>
                  ${(stats.ingresos_mes_actual / 1000).toFixed(0)}K
                </div>
                <p className="text-sm text-white/80 mt-2 flex items-center gap-1">
                  <ArrowUpRight className="h-4 w-4" />
                  ${stats.ingresos_mes_actual.toLocaleString()} COP
                </p>
              </CardContent>
            </Card>

            <Card className="stat-card border-none bg-gradient-to-br from-slate-500 to-slate-600 text-white" data-testid="stat-card-reservas-total">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-white/90">
                  Total Reservas
                </CardTitle>
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <Calendar className="h-5 w-5 text-white" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold" style={{fontFamily: 'Manrope'}}>
                  {stats.total_reservas}
                </div>
                <p className="text-sm text-white/80 mt-2">
                  Todas las reservas
                </p>
              </CardContent>
            </Card>

            <Card className="stat-card border-none bg-gradient-to-br from-blue-400 to-blue-500 text-white" data-testid="stat-card-habitaciones">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-white/90">
                  Habitaciones
                </CardTitle>
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <Hotel className="h-5 w-5 text-white" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold" style={{fontFamily: 'Manrope'}}>
                  {stats.total_habitaciones}
                </div>
                <p className="text-sm text-white/80 mt-2">
                  Total disponibles
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Activity Cards */}
        <div className="grid gap-6 md:grid-cols-2">
          <Card className="border-l-4 border-l-blue-500 bg-gradient-to-r from-blue-50 to-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{fontFamily: 'Manrope'}}>
                <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                  <ArrowUpRight className="h-5 w-5 text-white" />
                </div>
                Próximos Check-ins
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Próximos 7 días</p>
                      <p className="text-xs text-muted-foreground">Check-ins programados</p>
                    </div>
                    <div className="text-4xl font-bold text-slate-800" style={{fontFamily: 'Manrope'}}>
                      {stats.proximos_checkins}
                    </div>
                  </div>
                  <Button 
                    className="w-full bg-slate-800 hover:bg-slate-700"
                    onClick={() => navigate('/reservations')}
                    data-testid="view-checkins-btn"
                  >
                    Ver Reservas
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-slate-500 bg-gradient-to-r from-slate-50 to-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{fontFamily: 'Manrope'}}>
                <div className="w-8 h-8 bg-slate-500 rounded-lg flex items-center justify-center">
                  <ArrowDownRight className="h-5 w-5 text-white" />
                </div>
                Próximos Check-outs
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Próximos 7 días</p>
                      <p className="text-xs text-muted-foreground">Check-outs programados</p>
                    </div>
                    <div className="text-4xl font-bold text-slate-600" style={{fontFamily: 'Manrope'}}>
                      {stats.proximos_checkouts}
                    </div>
                  </div>
                  <Button 
                    className="w-full bg-slate-600 hover:bg-slate-700"
                    onClick={() => navigate('/reservations')}
                    data-testid="view-checkouts-btn"
                  >
                    Ver Reservas
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle style={{fontFamily: 'Manrope'}}>Acciones Rápidas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Button 
                variant="outline" 
                className="h-auto py-4 flex flex-col gap-2"
                onClick={() => navigate('/calendar')}
                data-testid="quick-action-calendar-btn"
              >
                <Calendar className="h-6 w-6" />
                <span>Ver Calendario</span>
              </Button>
              <Button 
                variant="outline" 
                className="h-auto py-4 flex flex-col gap-2"
                onClick={() => navigate('/rates')}
                data-testid="quick-action-rates-btn"
              >
                <DollarSign className="h-6 w-6" />
                <span>Gestionar Tarifas</span>
              </Button>
              <Button 
                variant="outline" 
                className="h-auto py-4 flex flex-col gap-2"
                onClick={() => navigate('/guests')}
                data-testid="quick-action-guests-btn"
              >
                <Users className="h-6 w-6" />
                <span>Ver Huéspedes</span>
              </Button>
              <Button 
                variant="outline" 
                className="h-auto py-4 flex flex-col gap-2"
                onClick={() => navigate('/ota-connections')}
                data-testid="quick-action-ota-btn"
              >
                <Hotel className="h-6 w-6" />
                <span>Conectar OTAs</span>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
